import { Controller } from '@hotwired/stimulus';
import 'chart.js/dist/chart.min';

export default class extends Controller {
  static values = { twelveMonths: Object, total: Object, labelTwelveMonths: String, labelTotal: String }
  static targets = [
    'canvas', 'kpiAmount', 'kpiLabel', 'kpiIconBg',
    'twelveMonthsBtn', 'totalBtn', 'emptyMessage',
    'tooltip', 'tooltipValue', 'tooltipDetail'
  ]

  connect() {
    this.currentScope = 'total'
    this.currentDetail = this.totalValue.detail || null
    this.showData(this.totalValue)
  }

  // Scope toggle (12 months / total)
  showTwelveMonths() {
    this.currentScope = 'twelveMonths'
    this.currentDetail = this.twelveMonthsValue.detail || null
    this.showData(this.twelveMonthsValue)
    this.updateKpi(this.twelveMonthsValue.total_amount, this.labelTwelveMonthsValue)
    this.updateButtons('twelveMonths')
  }

  showTotal() {
    this.currentScope = 'total'
    this.currentDetail = this.totalValue.detail || null
    this.showData(this.totalValue)
    this.updateKpi(this.totalValue.total_amount, this.labelTotalValue)
    this.updateButtons('total')
  }

  // Chart data & empty state
  showData(data) {
    const isEmpty = !!data.empty

    this.emptyMessageTarget.classList.toggle('d-none', !isEmpty)
    this.updateEmptyStyle(isEmpty)

    if (!this.chart) {
      this.initChart(data)
    } else {
      this.chart.data = data
      this.chart.update()
    }
  }

  updateEmptyStyle(isEmpty) {
    const bg = this.kpiIconBgTarget
    const icon = bg.querySelector('svg')

    bg.classList.toggle('bg-secondary', !isEmpty)
    bg.classList.toggle('bg-gray-100', isEmpty)

    if (icon) {
      icon.classList.toggle('text-primary', !isEmpty)
      icon.classList.toggle('text-muted', isEmpty)
    }

    this.kpiAmountTarget.classList.toggle('text-muted', isEmpty)
    this.kpiLabelTarget.classList.toggle('text-dark', !isEmpty)
    this.kpiLabelTarget.classList.toggle('text-muted', isEmpty)
  }

  // Chart.js initialization
  initChart(data) {
    const self = this
    const config = {
      type: 'bar',
      data: data,
      options: {
        responsive: true,
        maintainAspectRatio: true,
        scales: {
          x: {
            type: 'category',
            ticks: {
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: window.innerWidth < 1500 ? 6 : 12,
              color: '#333333',
            },
            grid: { color: '#F6F6F6', borderColor: '#F6F6F6' },
          },
          y: {
            beginAtZero: true,
            ticks: {
              color: '#333333',
              callback: (value) => `${new Intl.NumberFormat('fr-FR').format(value)} €`
            },
            grid: { color: '#F6F6F6', borderColor: '#F6F6F6' },
          }
        },
        borderRadius: 4,
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: (ctx) => self.externalTooltip(ctx)
          }
        }
      }
    }
    this.chart = new Chart(this.canvasTarget, config)
  }

  // KPI display
  updateKpi(amount, label) {
    this.kpiAmountTarget.textContent = this.formatCurrency(amount || 0, 2)
    if (label) this.kpiLabelTarget.textContent = label
  }

  // Custom tooltip

  externalTooltip(context) {
    const el = this.tooltipTarget
    const tooltip = context.tooltip

    if (tooltip.opacity === 0) {
      el.classList.add('d-none')
      return
    }

    const label = tooltip.dataPoints[0].label
    const value = tooltip.dataPoints[0].raw

    this.tooltipValueTarget.textContent = this.formatCurrency(value, 2)
    this.updateTooltipDetail(label)

    el.classList.remove('d-none')
    el.offsetWidth // force reflow to get correct dimensions

    // Position
    const container = this.canvasTarget.parentElement
    const canvasRect = this.canvasTarget.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()
    const left = canvasRect.left - containerRect.left + tooltip.caretX
    const top = canvasRect.top - containerRect.top + tooltip.caretY

    el.style.top = `${top}px`
    el.style.transform = 'translateY(calc(-100% + 2px))'

    // Clamp horizontal position (use card width to allow tooltip near edges)
    const cardWidth = this.element.offsetWidth
    const cardOffset = containerRect.left - this.element.getBoundingClientRect().left
    const tooltipWidth = el.offsetWidth
    const halfTooltip = tooltipWidth / 2
    const clampedLeft = Math.max(halfTooltip - cardOffset, Math.min(left, cardWidth - cardOffset - halfTooltip))
    el.style.left = `${clampedLeft}px`
    el.style.marginLeft = `${-halfTooltip}px`
  }

  updateTooltipDetail(label) {
    const detailEl = this.tooltipDetailTarget

    if (!this.currentDetail) {
      detailEl.classList.add('d-none')
      return
    }

    const detailItems = this.currentDetail[label]

    if (!detailItems || detailItems.length === 0) {
      detailEl.classList.add('d-none')
      return
    }

    const parts = detailItems.map((item) => `${item.label}: ${this.formatCurrency(item.amount, 0)}`).join(', ')
    detailEl.textContent = `(${parts})`
    detailEl.classList.remove('d-none')
  }

  formatCurrency(n, decimals) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(n)
  }

  // Toggle buttons (12 months / total)
  updateButtons(active) {
    const activeClasses = ['bg-primary', 'text-white', 'rounded-1']
    const inactiveClasses = ['text-dark']

    if (active === 'twelveMonths') {
      this.setActive(this.twelveMonthsBtnTarget, activeClasses, inactiveClasses)
      this.setActive(this.totalBtnTarget, inactiveClasses, activeClasses)
    } else {
      this.setActive(this.totalBtnTarget, activeClasses, inactiveClasses)
      this.setActive(this.twelveMonthsBtnTarget, inactiveClasses, activeClasses)
    }
  }

  setActive(btn, addClasses, removeClasses) {
    btn.classList.remove(...removeClasses)
    btn.classList.add(...addClasses)
  }
}
